/* =====================================================================
   Priming Sugar Calculator — Craft a Brew
   Self-contained: dynamically loads Lucide icons if not already present.
   ===================================================================== */

(function () {
  'use strict';

  function ensureLucide(cb) {
    if (window.lucide && window.lucide.createIcons) { cb(); return; }
    var existing = document.querySelector('script[data-cab-lucide]');
    if (existing) { existing.addEventListener('load', cb); return; }
    var s = document.createElement('script');
    s.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
    s.async = true;
    s.setAttribute('data-cab-lucide', '1');
    s.onload = cb;
    s.onerror = cb;
    document.head.appendChild(s);
  }

  function boot() { ensureLucide(function () { initApp(); }); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function initApp() {


  // ==========================================================
  // STATE
  // ==========================================================
  var state = {
    volUnit: 'gal',
    tempUnit: 'F',
    volumeGal: 5,
    tempF: 70,
    targetVols: 2.4,
    sugarType: 'corn_sugar',
    partialPct: 100,
    kegTempF: 38,
    bevFilter: 'all'
  };

  // ==========================================================
  // DATA
  // ==========================================================
  // Style list grouped by category. CO₂ volumes from BJCP
  // guidelines, "Designing Great Beers" (Ray Daniels), and
  // commonly-cited homebrew references.
  var STYLES = [
    { group: 'American Ales', type: 'beer', items: [
      { name: 'American Pale Ale', vols: 2.4 },
      { name: 'American IPA', vols: 2.4 },
      { name: 'Double IPA', vols: 2.2 },
      { name: 'NEIPA / Hazy IPA', vols: 2.4 },
      { name: 'Session IPA', vols: 2.4 },
      { name: 'Black IPA', vols: 2.4 },
      { name: 'American Amber Ale', vols: 2.3 },
      { name: 'American Brown Ale', vols: 2.4 },
      { name: 'American Porter', vols: 2.1 },
      { name: 'American Stout', vols: 2.1 },
      { name: 'Imperial Stout', vols: 2.2 },
      { name: 'Blonde Ale', vols: 2.6 },
      { name: 'Cream Ale', vols: 2.6 },
      { name: 'American Barleywine', vols: 2.1 }
    ]},
    { group: 'English & Irish Ales', type: 'beer', items: [
      { name: 'British Bitter', vols: 1.5 },
      { name: 'Extra Special Bitter (ESB)', vols: 1.7 },
      { name: 'English Pale Ale', vols: 1.9 },
      { name: 'English Brown Ale', vols: 2.1 },
      { name: 'English Porter', vols: 2.0 },
      { name: 'Dry Irish Stout', vols: 1.9 },
      { name: 'Irish Red Ale', vols: 2.2 },
      { name: 'Mild Ale', vols: 1.8 },
      { name: 'Old Ale', vols: 2.2 },
      { name: 'Scottish 60/-', vols: 1.9 },
      { name: 'Scottish 80/-', vols: 2.0 },
      { name: 'Scottish Wee Heavy', vols: 2.1 },
      { name: 'English Barleywine', vols: 2.2 }
    ]},
    { group: 'German & Lagers', type: 'beer', items: [
      { name: 'German Pilsner', vols: 2.5 },
      { name: 'Czech Pilsner', vols: 2.3 },
      { name: 'Munich Helles', vols: 2.4 },
      { name: 'Munich Dunkel', vols: 2.4 },
      { name: 'Oktoberfest / M\u00e4rzen', vols: 2.5 },
      { name: 'Bock', vols: 2.5 },
      { name: 'Doppelbock', vols: 2.4 },
      { name: 'Maibock', vols: 2.3 },
      { name: 'Schwarzbier', vols: 2.3 },
      { name: 'German Altbier', vols: 2.3 },
      { name: 'K\u00f6lsch', vols: 2.7 },
      { name: 'Rauchbier', vols: 2.3 },
      { name: 'American Lager', vols: 2.6 },
      { name: 'California Common', vols: 2.6 }
    ]},
    { group: 'Wheat Beers', type: 'beer', items: [
      { name: 'American Wheat', vols: 2.6 },
      { name: 'German Hefeweizen', vols: 3.6 },
      { name: 'Dunkelweizen', vols: 3.2 },
      { name: 'Weizenbock', vols: 3.4 },
      { name: 'Witbier', vols: 3.0 },
      { name: 'Berliner Weisse', vols: 2.8 },
      { name: 'Roggenbier', vols: 3.1 }
    ]},
    { group: 'Belgian Styles', type: 'beer', items: [
      { name: 'Belgian Blond', vols: 2.9 },
      { name: 'Belgian Dubbel', vols: 3.0 },
      { name: 'Belgian Tripel', vols: 3.3 },
      { name: 'Belgian Quad', vols: 2.5 },
      { name: 'Saison', vols: 3.2 },
      { name: 'Golden Strong Ale', vols: 3.3 },
      { name: 'Bi\u00e8re de Garde', vols: 3.3 }
    ]},
    { group: 'Cider', type: 'cider', items: [
      { name: 'Still Cider', vols: 0.5 },
      { name: 'P\u00e9tillant Cider', vols: 1.5 },
      { name: 'English Cider', vols: 2.0 },
      { name: 'Sparkling Cider', vols: 3.0 }
    ]},
    { group: 'Mead', type: 'mead', items: [
      { name: 'Still Mead', vols: 0.5 },
      { name: 'P\u00e9tillant Mead', vols: 1.5 },
      { name: 'Sparkling Mead', vols: 3.2 }
    ]},
    { group: 'Soda & Seltzer', type: 'soda', items: [
      { name: 'Hard Seltzer', vols: 2.8 },
      { name: 'Root Beer', vols: 3.8 },
      { name: 'Craft Cola', vols: 3.8 },
      { name: 'Ginger Beer', vols: 3.5 }
    ]}
  ];

  // Sugar types with conversion factors (relative to corn sugar
  // monohydrate at 1.00). Factor represents mass of that sugar
  // needed for the same CO₂ output. Higher = need more.
  var SUGARS = [
    { id: 'corn_sugar',    name: 'Corn Sugar (Dextrose)',      factor: 1.00, note: 'The default. Clean, neutral, predictable.' },
    { id: 'table_sugar',   name: 'Table Sugar (Sucrose)',      factor: 0.87, note: 'Pantry staple, slightly more efficient per gram.' },
    { id: 'dme',           name: 'Dry Malt Extract (DME)',     factor: 1.46, note: 'Adds malt character and subtle body.' },
    { id: 'honey',         name: 'Honey',                       factor: 1.11, note: 'Adds soft floral sweetness.' },
    { id: 'maple_syrup',   name: 'Maple Syrup',                factor: 1.14, note: 'Subtle maple character. Great in porters.' },
    { id: 'belgian_candi', name: 'Belgian Candi Sugar',        factor: 0.91, note: 'Classic for Belgian styles.' },
    { id: 'brown_sugar',   name: 'Brown Sugar',                factor: 0.90, note: 'Faint molasses note.' },
    { id: 'turbinado',     name: 'Turbinado / Raw Sugar',      factor: 0.90, note: 'Similar to table sugar with trace molasses.' },
    { id: 'molasses',      name: 'Molasses',                    factor: 1.17, note: 'Strong flavor. Pair with dark beers only.' }
  ];

  // ==========================================================
  // CORE MATH
  // ==========================================================
  // Zahm & Nagel equation for residual CO₂ at given beer temp.
  // T in °F, returns volumes of dissolved CO₂.
  function residualCO2(tempF) {
    return 3.0378 - (0.050062 * tempF) + (0.00026555 * tempF * tempF);
  }

  // Priming sugar needed in grams.
  // 4.2 g/L/vol is the empirical baseline for corn sugar
  // (dextrose monohydrate), matching Palmer's tables, BYO
  // magazine, and MoreBeer. Slightly below the pure-stoichiometric
  // value (4.42) because commercial corn sugar isn't 100% pure
  // and fermentation isn't 100% efficient.
  function primingSugarGrams(volumeL, deltaVols, sugarFactor) {
    if (deltaVols <= 0) return 0;
    return 4.2 * volumeL * deltaVols * sugarFactor;
  }

  // Force-carbonation PSI formula. Polynomial approximation of
  // Henry's Law for CO₂ in beer, widely cited in homebrew calcs.
  // T in °F, V in target CO₂ volumes. Returns required head
  // pressure in PSI.
  function forceCarbPSI(tempF, targetVols) {
    return -16.6999
      - (0.0101059 * tempF)
      + (0.00116512 * tempF * tempF)
      + (0.173354 * tempF * targetVols)
      + (4.24267 * targetVols)
      - (0.0684226 * targetVols * targetVols);
  }

  // Bottle sizes (in liters) for per-bottle priming grid
  var BOTTLE_SIZES = [
    { id: 12,  label: '12 oz',  liters: 0.35490 }, // 12 US fl oz
    { id: 16,  label: '16 oz',  liters: 0.47318 },
    { id: 22,  label: '22 oz',  liters: 0.65062 },
    { id: 500, label: '500 ml', liters: 0.50000 },
    { id: 750, label: '750 ml', liters: 0.75000 }
  ];
  var GRAMS_PER_DROP = 3.3; // Coopers-style carbonation drop equivalent

  // ==========================================================
  // UNIT CONVERSIONS
  // ==========================================================
  function galToL(gal) { return gal * 3.78541; }
  function lToGal(L) { return L / 3.78541; }
  function fToC(f) { return (f - 32) * 5 / 9; }
  function cToF(c) { return c * 9 / 5 + 32; }
  function gToOz(g) { return g / 28.3495; }

  // ==========================================================
  // CARBONATION CATEGORY
  // ==========================================================
  function getCarbCategory(vols) {
    // Map 0-5 vols onto 0-100% position
    var position = Math.max(0, Math.min(100, (vols / 5.0) * 100));
    var desc;
    if (vols < 1.0)      desc = 'Still. Barely any carbonation. Traditional ciders, meads, cask ales.';
    else if (vols < 2.0) desc = 'Low carbonation. English cask, p\u00e9tillant ciders, milds.';
    else if (vols < 2.7) desc = 'Medium carbonation. Standard for most ales and lagers.';
    else if (vols < 3.3) desc = 'High carbonation. Belgian ales, pilsners, American wheats.';
    else if (vols < 4.0) desc = 'Very high carbonation. Hefeweizens, Belgian tripels, sodas.';
    else                 desc = 'Wild carbonation. Champagne territory. Specialty bottles only.';
    return { position: position, desc: desc };
  }

  // ==========================================================
  // BOTTLE SAFETY
  // ==========================================================
  function getSafety(vols) {
    if (vols <= 3.0) {
      return { level: 'safe', icon: 'shield-check',
        text: 'Safe for standard pry-off beer bottles (rated to ~3.0 volumes).' };
    } else if (vols <= 3.5) {
      return { level: 'caution', icon: 'shield-alert',
        text: 'Use thick-walled bottles or flip-tops. Standard pry-off bottles may fail at this level.' };
    } else if (vols <= 4.5) {
      return { level: 'caution', icon: 'shield-alert',
        text: 'Belgian or champagne bottles required. Standard beer bottles will likely burst.' };
    } else {
      return { level: 'danger', icon: 'alert-octagon',
        text: 'Extreme carbonation. Champagne bottles with wire cages only, or force-carbonate in a keg instead.' };
    }
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================
  function validate(tempF, volumeGal, targetVols, residual) {
    var w = [];
    if (!isFinite(tempF) || !isFinite(volumeGal) || !isFinite(targetVols)) return w;

    if (tempF < 32 || tempF > 100) {
      w.push({ type: 'caution', icon: 'alert-triangle',
        text: 'Temperature looks unusual. For ales, use ~65\u201375&deg;F; for lagers use the warmest point (usually diacetyl rest).' });
    }
    if (volumeGal <= 0) {
      w.push({ type: 'error', icon: 'alert-circle',
        text: 'Batch size must be greater than zero.' });
    }
    if (volumeGal > 100) {
      w.push({ type: 'caution', icon: 'alert-triangle',
        text: 'Large batch size. Double-check you meant this many.' });
    }
    if (targetVols < 0) {
      w.push({ type: 'error', icon: 'alert-circle', text: 'Target CO\u2082 must be zero or positive.' });
    }
    if (targetVols > 5) {
      w.push({ type: 'caution', icon: 'alert-triangle',
        text: 'Very high target (> 5 vols). Consider force-carbonating in a keg instead for safety.' });
    }
    if (residual >= targetVols && targetVols > 0) {
      w.push({ type: 'info', icon: 'info',
        text: 'Your beer already has more CO\u2082 dissolved than your target. No priming sugar needed, or raise your target.' });
    }
    return w;
  }

  // ==========================================================
  // STYLE DROPDOWN
  // ==========================================================
  function populateStyleSelect(preserveValue) {
    var sel = document.getElementById('styleSelect');
    var currentValue = preserveValue ? sel.value : 'American Pale Ale';
    var filtered = state.bevFilter === 'all'
      ? STYLES
      : STYLES.filter(function (g) { return g.type === state.bevFilter; });
    var html = '<option value="custom" data-vols="">Custom (set your own CO\u2082)</option>';
    var availableNames = [];
    filtered.forEach(function (group) {
      html += '<optgroup label="' + group.group + '">';
      group.items.forEach(function (item) {
        html += '<option value="' + item.name + '" data-vols="' + item.vols + '">' +
                item.name + ' (' + item.vols.toFixed(1) + ')</option>';
        availableNames.push(item.name);
      });
      html += '</optgroup>';
    });
    sel.innerHTML = html;

    // Try to keep current selection. If not available, pick first style
    // in the filtered list (or custom if filter yields no matches).
    if (currentValue && availableNames.indexOf(currentValue) !== -1) {
      sel.value = currentValue;
    } else if (availableNames.length) {
      sel.value = availableNames[0];
      // Sync vols input to the new default
      var firstItem = filtered[0].items[0];
      document.getElementById('volsInput').value = firstItem.vols.toFixed(1);
    } else {
      sel.value = 'custom';
    }
    updateStyleCategoryLabel();
  }

  function updateStyleCategoryLabel() {
    var sel = document.getElementById('styleSelect');
    var label = document.getElementById('styleCategoryLabel');
    if (sel.value === 'custom') {
      label.textContent = 'Custom';
      return;
    }
    // Find which group this style is in
    for (var i = 0; i < STYLES.length; i++) {
      for (var j = 0; j < STYLES[i].items.length; j++) {
        if (STYLES[i].items[j].name === sel.value) {
          label.textContent = STYLES[i].group;
          return;
        }
      }
    }
    label.textContent = '';
  }

  // ==========================================================
  // SUGAR DROPDOWN
  // ==========================================================
  function populateSugarDropdown() {
    var sel = document.getElementById('sugarDropdown');
    var html = '';
    SUGARS.forEach(function (s) {
      html += '<option value="' + s.id + '">' + s.name + '</option>';
    });
    sel.innerHTML = html;
    sel.value = state.sugarType;
  }

  // ==========================================================
  // RENDER
  // ==========================================================
  function render() {
    var sizeEl = document.getElementById('batchSize');
    var tempEl = document.getElementById('beerTemp');
    var volsEl = document.getElementById('volsInput');

    var sizeInput = parseFloat(sizeEl.value);
    var tempInput = parseFloat(tempEl.value);
    var targetVols = parseFloat(volsEl.value);
    if (isNaN(sizeInput)) sizeInput = 0;
    if (isNaN(tempInput)) tempInput = 70;
    if (isNaN(targetVols)) targetVols = 0;

    // Convert to canonical units (gal, F)
    var volumeGal = state.volUnit === 'L' ? lToGal(sizeInput) : sizeInput;
    var tempF = state.tempUnit === 'C' ? cToF(tempInput) : tempInput;

    state.volumeGal = volumeGal;
    state.tempF = tempF;
    state.targetVols = targetVols;

    var volumeL = galToL(volumeGal) * (state.partialPct / 100);
    var residual = residualCO2(tempF);
    var delta = targetVols - residual;

    // Main sugar amount
    var mainSugar = SUGARS.find(function (s) { return s.id === state.sugarType; });
    if (!mainSugar) mainSugar = SUGARS[0];
    var mainGrams = primingSugarGrams(volumeL, delta, mainSugar.factor);
    var mainOz = gToOz(mainGrams);

    // Format display
    document.getElementById('sugarOz').textContent = mainGrams <= 0 ? '\u2014' : mainOz.toFixed(1) + ' oz';
    document.getElementById('sugarGram').textContent = mainGrams <= 0 ? 'No sugar needed' : Math.round(mainGrams) + ' grams';
    document.getElementById('sugarLabel').textContent = mainSugar.name;

    // Stats
    document.getElementById('targetVolsDisp').textContent = targetVols.toFixed(1);
    document.getElementById('residualVolsDisp').textContent = Math.max(0, residual).toFixed(2);
    var deltaText = delta > 0 ? '+' + delta.toFixed(2) : delta.toFixed(2);
    document.getElementById('deltaVolsDisp').textContent = deltaText;

    // Carbonation bar
    var cat = getCarbCategory(targetVols);
    document.getElementById('carbFill').style.width = cat.position + '%';
    document.getElementById('carbMarker').style.left = cat.position + '%';
    document.getElementById('carbName').innerHTML = cat.desc;

    // Safety
    var safety = getSafety(targetVols);
    var safetyBox = document.getElementById('safetyBox');
    safetyBox.className = 'safety-box ' + safety.level;
    safetyBox.innerHTML = '<i data-lucide="' + safety.icon + '" class="cab-icon"></i>' +
                         '<span>' + safety.text + '</span>';

    // Alternative sugars table
    var altsHtml = '';
    SUGARS.forEach(function (s) {
      var g = primingSugarGrams(volumeL, delta, s.factor);
      var oz = gToOz(g);
      var gDisp = g <= 0 ? '\u2014' : Math.round(g) + ' g';
      var ozDisp = g <= 0 ? '\u2014' : oz.toFixed(1) + ' oz';
      altsHtml += '<div class="alt-row' + (s.id === state.sugarType ? ' active' : '') +
                  '" data-id="' + s.id + '">' +
                  '<div><div class="alt-name">' + s.name + '</div>' +
                  '<div class="alt-note">' + s.note + '</div></div>' +
                  '<div class="alt-gram">' + gDisp + '</div>' +
                  '<div class="alt-oz">' + ozDisp + '</div></div>';
    });
    document.getElementById('altsBody').innerHTML = altsHtml;

    // Wire clicks on alt rows
    document.querySelectorAll('.alt-row').forEach(function (row) {
      row.addEventListener('click', function () {
        state.sugarType = this.dataset.id;
        document.getElementById('sugarDropdown').value = state.sugarType;
        var selected = SUGARS.find(function (s) { return s.id === state.sugarType; });
        if (selected) document.getElementById('sugarValueLabel').textContent = selected.name;
        render();
      });
    });

    // Per-bottle breakdown — sugar per bottle for each common size,
    // using the currently selected sugar. Carb-drop equivalent uses
    // the corn-sugar-equivalent mass (drops are calibrated for corn
    // sugar, so we convert back).
    BOTTLE_SIZES.forEach(function (b) {
      var bottleGrams = primingSugarGrams(b.liters, delta, mainSugar.factor);
      var cornSugarEquiv = primingSugarGrams(b.liters, delta, 1.00);
      var drops = cornSugarEquiv / GRAMS_PER_DROP;
      var gEl = document.getElementById('bot' + b.id + 'g');
      var dEl = document.getElementById('bot' + b.id + 'd');
      if (gEl) gEl.textContent = bottleGrams <= 0 ? '\u2014' : bottleGrams.toFixed(1) + ' g';
      if (dEl) {
        if (drops <= 0) {
          dEl.textContent = '';
        } else {
          dEl.textContent = '\u2248 ' + drops.toFixed(1) + ' drop' + (Math.abs(drops - 1) < 0.05 ? '' : 's');
        }
      }
    });

    // Force-carbonation PSI — for kegging at a target temperature
    var psiVal = forceCarbPSI(state.kegTempF, targetVols);
    var psiEl = document.getElementById('psiValue');
    var psiCtx = document.getElementById('psiContext');
    if (psiEl) psiEl.textContent = psiVal <= 0 ? '\u2014' : psiVal.toFixed(1);
    if (psiCtx) psiCtx.textContent = 'for ' + targetVols.toFixed(1) + ' vols';

    // Warnings
    var warns = validate(tempF, volumeGal, targetVols, residual);
    var wBox = document.getElementById('warnings');
    if (warns.length) {
      wBox.innerHTML = warns.map(function (w) {
        return '<div class="warning ' + w.type + '">' +
               '<i data-lucide="' + w.icon + '" class="cab-icon"></i>' +
               '<span>' + w.text + '</span></div>';
      }).join('');
    } else {
      wBox.innerHTML = '';
    }

    // Re-render icons after DOM updates
    if (window.lucide && window.lucide.createIcons) window.lucide.createIcons();
  }

  // ==========================================================
  // UNIT TOGGLES
  // ==========================================================
  function switchVolUnit(newUnit) {
    if (newUnit === state.volUnit) return;
    var sizeEl = document.getElementById('batchSize');
    var current = parseFloat(sizeEl.value) || 0;
    if (state.volUnit === 'gal' && newUnit === 'L') {
      sizeEl.value = galToL(current).toFixed(1);
    } else if (state.volUnit === 'L' && newUnit === 'gal') {
      sizeEl.value = lToGal(current).toFixed(2);
    }
    state.volUnit = newUnit;
    document.getElementById('batchUnit').textContent = newUnit === 'L' ? 'liters' : 'gallons';
    sizeEl.step = newUnit === 'L' ? '0.5' : '0.1';
    render();
  }

  function switchTempUnit(newUnit) {
    if (newUnit === state.tempUnit) return;
    var tempEl = document.getElementById('beerTemp');
    var kegEl = document.getElementById('kegTemp');
    var current = parseFloat(tempEl.value) || 0;
    var kegCurrent = parseFloat(kegEl.value) || 0;
    if (state.tempUnit === 'F' && newUnit === 'C') {
      tempEl.value = Math.round(fToC(current));
      kegEl.value = Math.round(fToC(kegCurrent));
    } else if (state.tempUnit === 'C' && newUnit === 'F') {
      tempEl.value = Math.round(cToF(current));
      kegEl.value = Math.round(cToF(kegCurrent));
    }
    state.tempUnit = newUnit;
    var unitLabel = newUnit === 'C' ? '&deg;C' : '&deg;F';
    document.getElementById('tempUnit').innerHTML = unitLabel;
    document.getElementById('kegTempUnit').innerHTML = unitLabel;
    render();
  }

  // ==========================================================
  // INIT
  // ==========================================================
  function bindToggleGroup(groupId, attrKey, onSelect) {
    var group = document.getElementById(groupId);
    if (!group) return;
    group.querySelectorAll('.toggle-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        group.querySelectorAll('.toggle-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        onSelect(btn.dataset[attrKey]);
      });
    });
  }

  function init() {
    populateStyleSelect();
    populateSugarDropdown();

    bindToggleGroup('volUnitToggle', 'vol', switchVolUnit);
    bindToggleGroup('tempUnitToggle', 'temp', switchTempUnit);

    document.getElementById('batchSize').addEventListener('input', render);
    document.getElementById('beerTemp').addEventListener('input', render);
    document.getElementById('volsInput').addEventListener('input', function () {
      // Switching to a custom vol deselects the style
      document.getElementById('styleSelect').value = 'custom';
      updateStyleCategoryLabel();
      render();
    });

    document.getElementById('styleSelect').addEventListener('change', function () {
      var opt = this.options[this.selectedIndex];
      var vols = parseFloat(opt.dataset.vols);
      if (isFinite(vols) && vols > 0) {
        document.getElementById('volsInput').value = vols.toFixed(1);
      }
      updateStyleCategoryLabel();
      render();
    });

    document.getElementById('sugarDropdown').addEventListener('change', function () {
      state.sugarType = this.value;
      var selected = SUGARS.find(function (s) { return s.id === state.sugarType; });
      if (selected) document.getElementById('sugarValueLabel').textContent = selected.name;
      render();
    });

    // Beverage filter pills
    var bevPills = document.getElementById('bevPills');
    if (bevPills) {
      bevPills.querySelectorAll('.bev-pill').forEach(function (btn) {
        btn.addEventListener('click', function () {
          bevPills.querySelectorAll('.bev-pill').forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          state.bevFilter = btn.dataset.bev;
          populateStyleSelect(true);
          // After repopulating, sync the vols input to the new selection
          var sel = document.getElementById('styleSelect');
          var opt = sel.options[sel.selectedIndex];
          var vols = parseFloat(opt && opt.dataset.vols);
          if (isFinite(vols) && vols >= 0) {
            document.getElementById('volsInput').value = vols.toFixed(1);
          }
          render();
        });
      });
    }

    document.getElementById('partialSlider').addEventListener('input', function () {
      state.partialPct = parseInt(this.value, 10);
      document.getElementById('partialValue').textContent = state.partialPct + '%';
      render();
    });

    document.getElementById('kegTemp').addEventListener('input', function () {
      var v = parseFloat(this.value);
      if (!isFinite(v)) return;
      state.kegTempF = state.tempUnit === 'C' ? cToF(v) : v;
      render();
    });

    var advBtn = document.getElementById('advancedToggle');
    var advPanel = document.getElementById('advancedPanel');
    advBtn.addEventListener('click', function () {
      var isOpen = advBtn.classList.toggle('open');
      advPanel.classList.toggle('open', isOpen);
      advBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    var altsToggle = document.getElementById('altsToggle');
    var altsWrap = document.getElementById('altsBodyWrap');
    altsToggle.addEventListener('click', function () {
      var isCollapsed = altsWrap.classList.toggle('collapsed');
      altsToggle.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
    });

    document.querySelectorAll('.tip-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        this.parentElement.style.display = 'none';
      });
    });

    if (window.lucide && window.lucide.createIcons) window.lucide.createIcons();
    render();
  }

    init();
  }
})();